import React, { useState, useEffect, Fragment } from 'react';
import _ from 'underscore';
import { Button, Wrapper, Menu, MenuItem } from 'react-aria-menubutton';
import classnames from 'classnames';
import { Portal } from 'react-portal';
import classNames from 'classnames';
import limitString from 'helpers/limit_string';

interface Option {
  value?: string | number;
  id?: number;
  name?: string;
  label?: string;
}

type Value = string | number;

interface MultipleSelectProps {
  value: Value[];
  options: Option[];
  onChange: (value: Value[]) => void;
  minWidth?: number;
  fullWidth?: boolean;
  alignLeft?: boolean;
  appendToBody?: boolean;
  placeholder?: string;
  emptySelectsAll?: boolean;
  disabled?: boolean;
  hideEmptyOption?: true;
  size?: 'sm';
  emptyClearsAll?: boolean;
}

const MultipleSelect = ({
  value = [],
  options,
  onChange,
  minWidth,
  fullWidth,
  alignLeft,
  appendToBody,
  placeholder = 'Show all',
  emptySelectsAll,
  disabled,
  hideEmptyOption,
  size,
  emptyClearsAll,
}: MultipleSelectProps) => {
  const [ref, setRef] = useState(null);
  const [coords, setCoords] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (options.length) {
      const updated = value.filter((item) =>
        _.find(options, (option) => getOptionValue(option) === item),
      );
      if (updated.length !== value.length) {
        onChange(updated);
      }
    }
  }, [value, options, onChange]);

  const [key, setKey] = useState(Math.floor(Math.random() * 10000));
  useEffect(() => {
    setKey(Math.floor(Math.random() * 10000));
  }, [onChange]);

  const [open, setOpen] = useState(false);

  const wrapperClass = classnames({
    'btn-group': true,
    open: open,
  });

  const onMenuToggle = ({ isOpen }) => {
    const { right, left, bottom: y } = ref.getBoundingClientRect();
    const x = alignLeft ? left : window.innerWidth - right;
    setCoords({ x, y: y + window.scrollY });
    setOpen(isOpen);
  };

  const getOptionValue = (option) => {
    return option.value || option.id;
  };

  const getOptionLabel = (option) => {
    return option.label || option.name;
  };

  const getLabel = () => {
    if (!value.length || (emptySelectsAll && value.length === options.length)) {
      return placeholder;
    }

    const selectedString = options
      .filter((option) => isSelected(getOptionValue(option)))
      .map(getOptionLabel)
      .join(', ');

    return fullWidth ? selectedString : limitString(selectedString, 15);
  };

  const getOptions = () =>
    options.map((option) => ({
      ...option,
      value: getOptionValue(option),
    }));

  const _onSelect = (val) => {
    if (val.type === 'a') {
      onChange([]);
      return;
    }
    if (allOptionsSelected()) {
      onChange([val]);
      return;
    }

    const newVal = isSelected(val) ? value.filter((v) => v !== val) : [...value, val];
    onChange(newVal);
  };

  const allOptionsSelected = () => {
    return emptySelectsAll && (!value.length || value.length === options.length);
  };

  const isSelected = (val: string | number) => {
    return !allOptionsSelected() && Array.isArray(value) && value.indexOf(val) >= 0;
  };

  useEffect(() => {
    if (options.length <= 1 || !value || !emptyClearsAll) return;
    if (value.length === options.length && emptyClearsAll) {
      onChange([]);
    }
  }, [value]);

  const MenuWrapper = appendToBody ? Portal : Fragment;

  return (
    <Wrapper
      onSelection={_onSelect}
      key={key}
      onMenuToggle={onMenuToggle}
      className={wrapperClass}
      style={{ marginRight: 0, width: fullWidth ? '100%' : undefined }}
    >
      <Button
        ref={setRef}
        tag="button"
        className={classNames('btn dropdown-toggle dropdown-toggle--v2', {
          'dropdown-toggle--sm': size === 'sm',
          'is-right': !alignLeft,
        })}
        style={{ minWidth, width: fullWidth ? '100%' : undefined }}
        disabled={disabled}
      >
        {getLabel()} <i className="caret"></i>
      </Button>

      <MenuWrapper>
        <Menu>
          <ul
            className="dropdown-menu dropdown-menu--v2 dropdown-menu--word-break dropdown-menu--in-portal"
            style={
              appendToBody
                ? {
                    top: coords.y,
                    right: alignLeft ? 'unset' : coords.x,
                    left: alignLeft ? coords.x : 'unset',
                  }
                : undefined
            }
          >
            {!hideEmptyOption && (
              <li>
                <MenuItem value={undefined}>
                  <a>
                    {allOptionsSelected() && <i className="icon-checkmark" />}
                    {placeholder}
                  </a>
                </MenuItem>
              </li>
            )}
            {getOptions().map((option, index) => (
              <li key={index}>
                <MenuItem value={option.value}>
                  <a>
                    {isSelected(option.value) && <i className="icon-checkmark" />}

                    {getOptionLabel(option)}
                  </a>
                </MenuItem>
              </li>
            ))}
          </ul>
        </Menu>
      </MenuWrapper>
    </Wrapper>
  );
};

export default MultipleSelect;
